import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
    honeypot: "" // Anti-spam honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypes = [
    "Aménagement intérieur",
    "Aménagement extérieur",
    "Mobilier",
    "Rénovation",
    "Autres"
  ];


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Demande envoyée avec succès !", {
        description: "Nous vous recontacterons dans les plus brefs délais."
      });
      // Redirect to thank-you page
      navigate("/merci");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue", {
        description: "Veuillez réessayer ou nous contacter directement."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 bg-warm-cream relative"
      data-testid="contact-section">

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-forest-green font-medium text-sm uppercase tracking-wider">
            Contact
          </span>
          <h2
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-deep-oak mt-3 mb-6"
            data-testid="contact-title">

            Parlons de Votre Projet
          </h2>
          <p className="text-text-secondary text-base md:text-lg">
            Vous avez un projet d'agencement ? Contactez-nous pour un devis 
            gratuit et personnalisé. Nous vous répondons sous 72h.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="font-heading text-2xl font-semibold text-deep-oak mb-6">
                Nos Coordonnées
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-deep-oak/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-deep-oak" />
                  </div>
                  <div>
                    <p className="font-medium text-deep-oak">Adresse</p>
                    <p className="text-text-secondary text-sm">
                      Villard-Bonnot (38190)
                      <br />
                      Isère, France
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-deep-oak/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-deep-oak" />
                  </div>
                  <div>
                    <p className="font-medium text-deep-oak">Email</p>
                    <a
                      href="mailto:contact@ttwood38.fr"
                      className="text-text-secondary text-sm hover:text-deep-oak transition-colors"
                      data-testid="email-link">ttwood38@gmail.com


                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-deep-oak/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-deep-oak" />
                  </div>
                  <div>
                    <p className="font-medium text-deep-oak">Téléphone</p>
                    <p className="text-text-secondary text-sm">
                      Sur demande
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="rounded-xl overflow-hidden border border-gray-200 h-48 bg-white">
              <iframe
                title="TTWood Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22582.77944544556!2d5.8744!3d45.2545!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478af4b3e5a79b9f%3A0x408ab2ae4bac570!2s38190%20Villard-Bonnot!5e0!3m2!1sfr!2sfr!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade" />

            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100">
              <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-deep-oak mb-2">
                        Nom complet *
                      </label>
                      <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Votre nom"
                      data-testid="input-name" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-deep-oak mb-2">
                        Email *
                      </label>
                      <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="votre@email.com"
                      data-testid="input-email" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-deep-oak mb-2">
                        Téléphone
                      </label>
                      <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="06 XX XX XX XX"
                      data-testid="input-phone" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-deep-oak mb-2">
                        Type de projet
                      </label>
                      <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleChange}
                      className="input-field"
                      data-testid="select-project-type">

                        <option value="">Sélectionnez un type</option>
                        {projectTypes.map((type) =>
                      <option key={type} value={type}>
                            {type}
                          </option>
                      )}
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-deep-oak mb-2">
                      Votre message *
                    </label>
                    <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="input-field resize-none"
                    placeholder="Décrivez votre projet, vos besoins, vos contraintes..."
                    data-testid="textarea-message" />

                  </div>

                  {/* Honeypot anti-spam field - hidden from users */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    className="absolute -left-[9999px] opacity-0 h-0 w-0"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full mt-8 flex items-center justify-center gap-2"
                  data-testid="submit-btn">

                    {isSubmitting ?
                  <>
                        <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        Envoi en cours...
                      </> :

                  <>
                        <Send className="w-5 h-5" />
                        Envoyer ma demande
                      </>
                  }
                  </button>

                  <p className="text-text-muted text-xs text-center mt-4">
                    En soumettant ce formulaire, vous acceptez d'être recontacté 
                    concernant votre projet.
                  </p>
                </form>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default ContactSection;